import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  X, Activity, Ticket, HardDrive, BookOpen, CalendarClock, StickyNote,
  AlertTriangle, ExternalLink, Home, Building2, Mail, Phone,
  KeyRound, MonitorDot, Pencil, Sparkles,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import CompanyLogo from './CompanyLogo';
import PathHealthChip from './PathHealthChip';
import { useAuth } from '../contexts/AuthContext';

// "Ficha del cliente" — todo lo que el agente necesita para atender, en un panel.
// Se abre desde la conversación de WhatsApp (y sirve igual desde un ticket).
// Read-only: es contexto para responder más rápido, no una pantalla de gestión.

const DOT = { ok: '#22c55e', degraded: '#f59e0b', down: '#ef4444', unknown: '#94a3b8' };
const STATUS_COLORS = { open: '#3b82f6', pending: '#f59e0b', resolved: '#10b981', closed: '#6b7280' };

function groupState(g) {
  if (!g || !g.total) return 'unknown';
  if (g.critical > 0) return 'down';
  if (g.warning > 0) return 'degraded';
  if (g.healthy > 0) return 'ok';
  return 'unknown';
}

function Section({ icon, title, right, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
        <span style={{ color: 'var(--c-brand)', display: 'inline-flex' }}>{icon}</span>
        <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--c-text-muted)' }}>{title}</span>
        <span style={{ flex: 1 }} />
        {right}
      </div>
      {children}
    </div>
  );
}

export default function ClientContextPanel({ companyId, contactId, onClose }) {
  const { t, i18n } = useTranslation();
  const { hasModule } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [editingNote, setEditingNote] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [draftingNote, setDraftingNote] = useState(false);

  const saveNote = async () => {
    setSavingNote(true);
    try {
      const { data: saved } = await api.put(`/client-context/${companyId}/note`, { notes: noteDraft });
      setData((prev) => (prev ? { ...prev, company: { ...prev.company, notes: saved.notes } } : prev));
      setEditingNote(false);
      toast.success(t('common.saved'));
    } catch {
      toast.error(t('common.actionFailed'));
    } finally {
      setSavingNote(false);
    }
  };

  // Propose-then-confirm: la IA sólo rellena el textarea; guardar sigue siendo un acto humano.
  const draftNote = async () => {
    setDraftingNote(true);
    try {
      const { data: r } = await api.post(`/client-context/${companyId}/note/draft`);
      if (r.draft) setNoteDraft(r.draft);
      else toast.error(t('common.actionFailed'));
    } catch (e) {
      toast.error(e.response?.status === 429 ? t('clientContext.aiCap') : t('common.actionFailed'));
    } finally {
      setDraftingNote(false);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const qs = contactId ? `?contactId=${encodeURIComponent(contactId)}` : '';
        const { data } = await api.get(`/client-context/${companyId}${qs}`);
        if (alive) setData(data);
      } catch {
        if (alive) setError(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [companyId, contactId]);

  // Esc cierra
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const locale = i18n.language === 'en' ? 'en-US' : 'es-AR';
  const fmt = (d) => d ? new Date(d).toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const go = (path) => { onClose(); navigate(path); };

  const co = data?.company;
  const down = data?.down || [];
  const techSummary = data?.techSummary || {};

  const modal = (
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1200, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.5)', padding: 16,
      }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        className="card"
        style={{ margin: 0, maxWidth: 720, width: '100%', maxHeight: '88vh', overflowY: 'auto', padding: 0 }}
      >
        {/* ── Header: quién es y de dónde ── */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px',
          borderBottom: '1px solid var(--c-border)', position: 'sticky', top: 0,
          background: 'var(--c-surface)', zIndex: 2,
        }}>
          {co && <CompanyLogo name={co.fantasy_name || co.name} logoUrl={co.logo_url} size={40} />}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 6 }}>
              {co?.fantasy_name || co?.name || t('clientContext.title')}
              {co?.is_personal
                ? <Home size={13} style={{ color: 'var(--c-text-muted)' }} title={t('clientContext.isHome')} />
                : <Building2 size={13} style={{ color: 'var(--c-text-muted)' }} />}
            </div>
            {data?.contact && (
              <div style={{ fontSize: 12.5, color: 'var(--c-text-muted)', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <span><b style={{ color: 'var(--c-text)' }}>{data.contact.name}</b>{data.contact.position ? ` · ${data.contact.position}` : ''}</span>
                {data.contact.phone && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><Phone size={11} />{data.contact.phone}</span>}
                {data.contact.email && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><Mail size={11} />{data.contact.email}</span>}
              </div>
            )}
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose} title={t('common.close')}><X size={18} /></button>
        </div>

        <div style={{ padding: 20 }}>
          {loading && <div className="spinner" />}
          {error && <p style={{ color: 'var(--c-text-muted)' }}>{t('clientContext.error')}</p>}

          {data && !loading && (
            <>
              {/* ── Lo caído AHORA: lo primero que tiene que ver el agente ── */}
              {down.length > 0 && (
                <div style={{
                  marginBottom: 18, padding: '12px 14px', borderRadius: 10,
                  border: '1px solid #ef4444', background: 'rgba(239,68,68,0.08)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6, color: '#ef4444', fontWeight: 800, fontSize: 13 }}>
                    <AlertTriangle size={15} /> {t('clientContext.downNow')}
                  </div>
                  {down.map((d, i) => (
                    <div key={i} style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 7, padding: '2px 0' }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: d.status === 'critical' ? '#ef4444' : '#f59e0b', flexShrink: 0 }} />
                      <b>{d.name}</b>
                      <span style={{ color: 'var(--c-text-muted)' }}>{d.asset_group || ''}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Nota de contexto (companies.notes) — la mitad ESCRITA A MANO de la
                     ficha: sólo lo que no se puede derivar de la base (quién decide, cómo
                     se trabaja, reglas). Se edita acá mismo porque es donde el agente
                     aprende esas cosas; mandarlo a otra pantalla es por qué faltaban. ── */}
              <div style={{
                marginBottom: 18, padding: '12px 14px', borderRadius: 10,
                border: '1px solid color-mix(in srgb, var(--c-brand) 30%, var(--c-border))',
                background: 'color-mix(in srgb, var(--c-brand) 8%, var(--c-surface))',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6, fontWeight: 800, fontSize: 13 }}>
                  <StickyNote size={15} style={{ color: 'var(--c-brand)' }} /> {t('clientContext.contextNote')}
                  <span style={{ flex: 1 }} />
                  {!editingNote && (
                    <button className="btn btn-ghost btn-sm" style={{ padding: '2px 8px', fontSize: 11 }}
                      onClick={() => { setNoteDraft(co?.notes || ''); setEditingNote(true); }}>
                      <Pencil size={12} /> {co?.notes ? t('common.edit') : t('clientContext.addNote')}
                    </button>
                  )}
                </div>

                {!editingNote && (
                  co?.notes
                    ? <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{co.notes}</div>
                    : <div style={{ fontSize: 12.5, color: 'var(--c-text-muted)' }}>{t('clientContext.noNoteHint')}</div>
                )}

                {editingNote && (
                  <>
                    <textarea
                      className="form-textarea"
                      rows={5}
                      value={noteDraft}
                      onChange={(e) => setNoteDraft(e.target.value)}
                      placeholder={t('clientContext.notePlaceholder')}
                      style={{ width: '100%', fontSize: 13 }}
                    />
                    <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                      <button className="btn btn-primary btn-sm" onClick={saveNote} disabled={savingNote}>
                        {savingNote ? t('common.saving') : t('common.save')}
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditingNote(false)} disabled={savingNote}>
                        {t('common.cancel')}
                      </button>
                      <span style={{ flex: 1 }} />
                      {/* Borrador IA: propone, nunca guarda solo. */}
                      <button className="btn btn-secondary btn-sm" onClick={draftNote} disabled={draftingNote}>
                        <Sparkles size={12} /> {draftingNote ? t('clientContext.drafting') : t('clientContext.aiDraft')}
                      </button>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--c-text-muted)', marginTop: 6 }}>
                      {t('clientContext.noteScopeHint')}
                    </div>
                  </>
                )}
              </div>

              {/* ── Resumen técnico DERIVADO: sedes + equipos de red, siempre en vivo
                     desde la base. Por esto la nota no necesita repetir inventario. ── */}
              {(techSummary.locations?.length > 0 || techSummary.infra?.length > 0) && (
                <Section icon={<Building2 size={15} />} title={t('clientContext.techSummary')}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {techSummary.locations?.map((l) => (
                      <span key={`loc-${l}`} style={{
                        fontSize: 12, padding: '4px 9px', borderRadius: 6,
                        border: '1px solid var(--c-border)', background: 'var(--c-surface-2)',
                      }}>📍 {l}</span>
                    ))}
                    {techSummary.infra?.map((d) => (
                      <span key={`inf-${d.kind}`} style={{
                        fontSize: 12, padding: '4px 9px', borderRadius: 6,
                        border: '1px solid var(--c-border)', background: 'var(--c-surface-2)',
                      }}>{d.n} {t(`clientContext.deviceKind.${d.kind}`, d.kind)}</span>
                    ))}
                  </div>
                </Section>
              )}

              {/* ── ¿Miramos a esta empresa por un túnel NUESTRO, y está sano? ──
                  Va ARRIBA del estado de sistemas a propósito: si el camino está
                  roto, todo lo de abajo ("N equipos caídos") es nuestra ceguera y
                  no la realidad del cliente, y el agente tiene que leer eso ANTES
                  de decirle nada al cliente (Casa Superí, ago-2026). El chip se
                  auto-oculta para empresas que no dependen de un túnel. */}
              {data.pathHealth?.tunnelDependent && (
                <Section icon={<Activity size={15} />} title={t('monitor.pathHealth.title')}>
                  <PathHealthChip path={data.pathHealth} />
                </Section>
              )}

              {/* ── Estado de sistemas por zona ── */}
              {data.systems?.length > 0 && (
                <Section icon={<Activity size={15} />} title={t('clientContext.systems')}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {data.systems.map((g) => {
                      const st = groupState(g);
                      return (
                        <div key={g.group} style={{
                          display: 'flex', alignItems: 'center', gap: 7, padding: '6px 11px',
                          border: '1px solid var(--c-border)', borderRadius: 999, fontSize: 12.5,
                        }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: DOT[st], flexShrink: 0 }} />
                          <span style={{ fontWeight: 600 }}>{t(`portal.dashboard.systems.groups.${g.group}`, g.group)}</span>
                          <span style={{ color: 'var(--c-text-muted)' }}>{g.healthy}/{g.total}</span>
                        </div>
                      );
                    })}
                  </div>
                </Section>
              )}

              {/* ── Tickets ── */}
              <Section
                icon={<Ticket size={15} />}
                title={t('clientContext.tickets')}
                right={<span style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>
                  {t('clientContext.openPending', { open: data.ticketCounts?.open || 0, pending: data.ticketCounts?.pending || 0 })}
                </span>}
              >
                {data.recentTickets?.length ? data.recentTickets.map((tk) => (
                  <div key={tk.id} onClick={() => go(`/tickets/${tk.id}`)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 9, padding: '6px 8px', borderRadius: 8,
                      cursor: 'pointer', fontSize: 13,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--c-surface-2)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: STATUS_COLORS[tk.status] || '#6b7280', flexShrink: 0 }} />
                    <span style={{ color: 'var(--c-text-muted)', fontVariantNumeric: 'tabular-nums' }}>#{tk.ticket_number}</span>
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tk.subject}</span>
                    <span style={{ color: 'var(--c-text-muted)', fontSize: 11.5 }}>{fmt(tk.created_at)}</span>
                  </div>
                )) : <p style={{ fontSize: 13, color: 'var(--c-text-muted)', margin: 0 }}>{t('clientContext.noTickets')}</p>}
              </Section>

              {/* ── Equipos ── */}
              {data.assets?.length > 0 && (
                <Section
                  icon={<HardDrive size={15} />}
                  title={t('clientContext.assets')}
                  right={<span style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>{data.assetCount} {t('clientContext.total')}</span>}
                >
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {data.assets.map((a) => (
                      <span key={a.id} style={{
                        fontSize: 12, padding: '4px 9px', borderRadius: 6,
                        border: '1px solid var(--c-border)', background: 'var(--c-surface-2)',
                      }}>
                        {a.name}{a.assigned_name ? <span style={{ color: 'var(--c-text-muted)' }}> · {a.assigned_name}</span> : ''}
                      </span>
                    ))}
                  </div>
                </Section>
              )}

              {/* ── Equipos con agente RMM: ¿la máquina está reportando ahora?
                     Gated por módulo: sin `monitoring` el link sería un callejón sin salida. ── */}
              {hasModule('monitoring') && data.endpoints?.length > 0 && (
                <Section icon={<MonitorDot size={15} />} title={t('clientContext.rmm')}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {data.endpoints.map((e) => {
                      const mins = e.last_seen_at ? (Date.now() - new Date(e.last_seen_at).getTime()) / 60000 : null;
                      const online = mins != null && mins < 10;
                      return (
                        <span key={e.id} onClick={() => go(`/monitoring/endpoints/${e.id}`)}
                          title={e.last_seen_at ? `${t('clientContext.lastSeen')}: ${fmt(e.last_seen_at)}` : t('clientContext.neverSeen')}
                          style={{
                            fontSize: 12, padding: '4px 9px', borderRadius: 6, cursor: 'pointer',
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            border: '1px solid var(--c-border)', background: 'var(--c-surface-2)',
                            fontWeight: e.is_theirs ? 700 : 400,
                          }}>
                          <span style={{ width: 7, height: 7, borderRadius: '50%', background: online ? DOT.ok : DOT.unknown, flexShrink: 0 }} />
                          {e.hostname}
                        </span>
                      );
                    })}
                  </div>
                </Section>
              )}

              {/* ── Vault: sólo el atajo + cuántas credenciales hay. El secreto se
                     revela en el módulo, con PIN y auditoría — nunca acá. ── */}
              {hasModule('vault') && data.vaultCount > 0 && (
                <Section icon={<KeyRound size={15} />} title={t('clientContext.vault')}>
                  <div
                    onClick={() => go(`/vault?company_id=${companyId}`)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 8,
                      cursor: 'pointer', fontSize: 13, border: '1px solid var(--c-border)', background: 'var(--c-surface-2)',
                    }}
                  >
                    <ExternalLink size={13} style={{ color: 'var(--c-brand)', flexShrink: 0 }} />
                    <span>{t('clientContext.vaultOpen', { count: data.vaultCount })}</span>
                    <span style={{ flex: 1 }} />
                    <span style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>{t('clientContext.vaultPin')}</span>
                  </div>
                </Section>
              )}

              {/* ── Guías de este cliente: para mandarle el link en el momento ── */}
              {data.guides?.length > 0 && (
                <Section icon={<BookOpen size={15} />} title={t('clientContext.guides')}>
                  {data.guides.map((g) => (
                    <div key={g.id} onClick={() => go(`/knowledge-base/${g.id}`)}
                      style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5px 8px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--c-surface-2)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                      <ExternalLink size={12} style={{ color: 'var(--c-brand)', flexShrink: 0 }} />
                      <span>{g.title}</span>
                    </div>
                  ))}
                </Section>
              )}

              {/* ── Visitas ── */}
              {(data.lastVisit || data.nextVisit) && (
                <Section icon={<CalendarClock size={15} />} title={t('clientContext.visits')}>
                  <div style={{ fontSize: 13, color: 'var(--c-text)' }}>
                    {data.nextVisit && <div>▸ <b>{t('clientContext.next')}:</b> {data.nextVisit.title || '—'} · {fmt(data.nextVisit.starts_at)}</div>}
                    {data.lastVisit && <div style={{ color: 'var(--c-text-muted)' }}>▸ {t('clientContext.last')}: {data.lastVisit.title || '—'} · {fmt(data.lastVisit.starts_at)}</div>}
                  </div>
                </Section>
              )}

              {/* ── Notas internas ── */}
              {data.notes?.length > 0 && (
                <Section icon={<StickyNote size={15} />} title={t('clientContext.notes')}>
                  {data.notes.map((n, i) => (
                    <div key={i} style={{
                      fontSize: 13, padding: '8px 10px', borderRadius: 8, marginBottom: 6,
                      background: 'var(--c-surface-2)', border: '1px solid var(--c-border)',
                    }}>
                      <div>{n.body}</div>
                      <div style={{ fontSize: 11, color: 'var(--c-text-muted)', marginTop: 3 }}>
                        {[n.first_name, n.last_name].filter(Boolean).join(' ')} · {fmt(n.created_at)}
                      </div>
                    </div>
                  ))}
                </Section>
              )}

              <button className="btn btn-secondary btn-sm" onClick={() => go(`/companies/${companyId}`)}>
                <ExternalLink size={13} /> {t('clientContext.openCompany')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}
